import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { isAddress } from 'viem'

import { getListedChainIds } from '~/constants/chains'
import { useActiveOrgSmartAccountsSetup } from '~/hooks/org'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'
import { getReadPublicClient } from '~/utils/requests'

const MAGIC_7702_PREFIX = '0xef0100'

/**
 * Hook that returns an async validation function compatible with react-hook-form.
 * The function leverages React-Query's cache so that each unique address is
 * checked against the network(s) at most once per staleTime window.
 *
 * Pass `excludeAddress` when editing an existing signer so the signer's own
 * address is not flagged as a duplicate.
 */
export const useRecoveryAddressValidation = (excludeAddress?: string) => {
  const queryClient = useQueryClient()
  const setup = useActiveOrgSmartAccountsSetup()

  return useCallback(
    async (value: string) => {
      if (!isAddress(value)) return false

      const checksumAddress = EvmAddress.format(value)
      const excludeChecksum = excludeAddress
        ? EvmAddress.format(excludeAddress)
        : undefined
      const existingSigners = setup?.eoaSigners ?? []
      const isDuplicate = existingSigners.some((s) => {
        const formatted = EvmAddress.format(s.address)
        return formatted === checksumAddress && formatted !== excludeChecksum
      })
      if (isDuplicate) return 'This address is already a recovery signer'

      try {
        const isValid = await queryClient.fetchQuery({
          queryKey: ['isValidRecoveryAddress', value],
          queryFn: () => validateRecoveryAddress(value as IAddress),
          staleTime: 1000 * 60 * 5, // 5 minutes
        })

        return isValid || 'Address cannot be a smart contract'
      } catch {
        return 'Could not validate address'
      }
    },
    [queryClient, setup?.eoaSigners, excludeAddress],
  )
}

/**
 * Actual validation routine that runs the multi-chain byte-code check.
 * Public so it can be reused in other contexts/tests.
 */
const validateRecoveryAddress = async (address: IAddress) => {
  const checksumAddress = EvmAddress.format(address)

  const multichainChecks = await Promise.all(
    getListedChainIds().map(async (chainId) => {
      const publicClient = getReadPublicClient(chainId)
      const byteCode = await publicClient.getCode({ address: checksumAddress })

      const is7702Account = byteCode?.startsWith(MAGIC_7702_PREFIX)
      const isValid = is7702Account || !byteCode // allow EOAs or 7702
      return isValid
    }),
  )

  return multichainChecks.every((check) => check === true)
}
